// D52b Part D — Task Templates manager.
// Settings UI for creating/editing/deleting reusable task templates.
// Templates support {client}, {client.short_name}, {month}, {year} placeholders.
import { useEffect, useState } from 'react';
import { sortClientsAlpha } from '../lib/sortClients';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { FileTextIcon, Plus, Trash2, Edit3, Loader2, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../contexts/WorkspaceContext';

const API = process.env.REACT_APP_BACKEND_URL;
const T = () => localStorage.getItem('stellar_token');
const PRIORITIES = ['low', 'medium', 'high'];

export default function TaskTemplatesPanel({ workspaceId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | template_id
  const [form, setForm] = useState(blank());
  // D54 B8 — Live preview state. Author picks a client → renders title/desc
  // for any template inline (resolves {client}, {client.short_name},
  // {month}, {year}) so they don't need to open the New Task modal.
  const [clients, setClients] = useState([]);
  const [previewClientId, setPreviewClientId] = useState('');
  const [previewFor, setPreviewFor] = useState(null); // { template_id, rendered, loading }

  function blank() {
    return {
      name: '',
      title_template: '',
      description_template: '',
      default_priority: 'medium',
      default_due_offset_days: 7,
      default_estimated_hours: 0,
      default_assignees: [],
      default_labels: [],
    };
  }

  const fetchTemplates = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/task-templates/${workspaceId}`, {
        headers: { Authorization: `Bearer ${T()}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); /* eslint-disable-next-line */ }, [workspaceId]);

  // D54 B8 — fetch clients for the live preview picker.
  useEffect(() => {
    if (!workspaceId) return;
    fetch(`${API}/api/client-groups/${workspaceId}`, {
      headers: { Authorization: `Bearer ${T()}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setClients(sortClientsAlpha(Array.isArray(d) ? d : [])))
      .catch(() => setClients([]));
  }, [workspaceId]);

  const renderPreview = async (templateId) => {
    setPreviewFor({ template_id: templateId, loading: true, rendered: null });
    try {
      const q = previewClientId ? `?client_group_id=${encodeURIComponent(previewClientId)}` : '';
      const r = await fetch(`${API}/api/task-templates/${templateId}/preview${q}`, {
        headers: { Authorization: `Bearer ${T()}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setPreviewFor({ template_id: templateId, loading: false, rendered: data.rendered });
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
      setPreviewFor(null);
    }
  };

  const startEdit = (tpl) => {
    setEditing(tpl.id);
    setForm({
      name: tpl.name || '',
      title_template: tpl.title_template || '',
      description_template: tpl.description_template || '',
      default_priority: tpl.default_priority || 'medium',
      default_due_offset_days: tpl.default_due_offset_days ?? 7,
      default_estimated_hours: tpl.default_estimated_hours || 0,
      default_assignees: tpl.default_assignees || [],
      default_labels: tpl.default_labels || [],
    });
  };

  const startNew = () => { setEditing('new'); setForm(blank()); };
  const cancel = () => { setEditing(null); setForm(blank()); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    try {
      const payload = {
        ...form,
        title_template: form.title_template || form.name,
        default_estimated_hours: Number(form.default_estimated_hours || 0),
        default_due_offset_days:
          form.default_due_offset_days === '' || form.default_due_offset_days === null
            ? null
            : Number(form.default_due_offset_days),
      };
      let r;
      if (editing === 'new') {
        r = await fetch(`${API}/api/task-templates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${T()}` },
          body: JSON.stringify({ ...payload, workspace_id: workspaceId }),
        });
      } else {
        r = await fetch(`${API}/api/task-templates/${editing}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${T()}` },
          body: JSON.stringify(payload),
        });
      }
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(editing === 'new' ? 'Template created' : 'Template saved');
      cancel();
      await fetchTemplates();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template? Tasks already created from it stay unchanged.')) return;
    try {
      const r = await fetch(`${API}/api/task-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${T()}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Template deleted');
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="task-templates-panel">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileTextIcon className="h-4 w-4 text-gold" /> Task Templates
          </CardTitle>
          <p className="text-[11px] text-muted-foreground/70 mt-1">
            Reusable templates with <span className="font-mono text-foreground/80">{'{client}'}</span>,{' '}
            <span className="font-mono text-foreground/80">{'{client.short_name}'}</span>,{' '}
            <span className="font-mono text-foreground/80">{'{month}'}</span>,{' '}
            <span className="font-mono text-foreground/80">{'{year}'}</span> placeholders.
          </p>
        </div>
        <Button size="sm" onClick={startNew} data-testid="task-templates-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Template
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* D54 B8 — Live preview client picker. When set, "Preview" button
            per row renders the resolved title/description inline. */}
        {clients.length > 0 && (
          <div className="flex items-center gap-2 pb-2 border-b border-border/20" data-testid="task-templates-preview-client-row">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60 shrink-0">Preview against</Label>
            <select
              value={previewClientId}
              onChange={(e) => { setPreviewClientId(e.target.value); setPreviewFor(null); }}
              className="flex-1 h-8 text-[12px] rounded-md border border-input bg-background px-2"
              data-testid="task-templates-preview-client"
            >
              <option value="">— Pick a client (placeholders stay literal otherwise) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground/70">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}

        {!loading && templates.length === 0 && (
          <div
            className="rounded-md border border-dashed border-border/40 px-3 py-6 text-center text-[12px] text-muted-foreground/70"
            data-testid="task-templates-empty"
          >
            No templates yet. Click <span className="font-mono">+ New Template</span> to add one.
          </div>
        )}

        {!loading && templates.map((tpl) => (
          <div key={tpl.id} data-testid={`task-template-row-wrap-${tpl.id}`}>
          <div
            className="grid grid-cols-12 gap-2 items-center rounded-md border border-border/30 bg-foreground/[0.02] px-3 py-2"
            data-testid={`task-template-row-${tpl.id}`}
          >
            <div className="col-span-7">
              <p className="text-[12px] font-medium text-foreground">{tpl.name}</p>
              {tpl.description_template && (
                <p className="text-[10px] text-muted-foreground/60 truncate" title={tpl.description_template}>
                  {tpl.description_template}
                </p>
              )}
            </div>
            <div className="col-span-2 text-center">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                tpl.default_priority === 'high'   ? 'bg-red-500/10 text-red-300 border-red-500/30' :
                tpl.default_priority === 'low'    ? 'bg-foreground/[0.05] text-muted-foreground/70 border-border/30' :
                                                    'bg-amber-500/10 text-amber-300 border-amber-500/30'
              }`}>
                {tpl.default_priority}
              </span>
            </div>
            <div className="col-span-1 text-[10px] text-muted-foreground/60 text-center font-mono">
              {tpl.default_due_offset_days != null ? `+${tpl.default_due_offset_days}d` : '—'}
            </div>
            <div className="col-span-2 flex justify-end gap-1">
              {/* D54 B8 — Live preview button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => previewFor?.template_id === tpl.id ? setPreviewFor(null) : renderPreview(tpl.id)}
                data-testid={`task-template-preview-${tpl.id}`}
                title={previewFor?.template_id === tpl.id ? 'Hide preview' : 'Preview rendered output'}
              >
                <Eye className={`h-3 w-3 ${previewFor?.template_id === tpl.id ? 'text-gold' : ''}`} />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(tpl)} data-testid={`task-template-edit-${tpl.id}`}>
                <Edit3 className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(tpl.id)} data-testid={`task-template-delete-${tpl.id}`}>
                <Trash2 className="h-3 w-3 text-red-400" />
              </Button>
            </div>
          </div>
          {/* D54 B8 — Inline preview render */}
          {previewFor?.template_id === tpl.id && (
            <div
              className="mt-1 ml-3 mr-3 rounded-md border border-gold/30 bg-gold/[0.04] px-3 py-2 space-y-1.5"
              data-testid={`task-template-preview-block-${tpl.id}`}
            >
              {previewFor.loading ? (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground/60">
                  <Loader2 className="h-3 w-3 animate-spin" /> Rendering…
                </div>
              ) : previewFor.rendered ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-wider text-gold/70 font-semibold">Preview</span>
                    {!previewClientId && (
                      <span className="text-[9px] text-amber-300/80">
                        ⚠ no client selected — {'{client}'} stays literal
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] font-medium text-foreground" data-testid={`task-template-preview-title-${tpl.id}`}>
                    {previewFor.rendered.title || <span className="italic text-muted-foreground/40">(no title)</span>}
                  </p>
                  {previewFor.rendered.description && (
                    <p className="text-[11px] text-muted-foreground/80 whitespace-pre-wrap" data-testid={`task-template-preview-description-${tpl.id}`}>
                      {previewFor.rendered.description}
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground/50 font-mono">
                    priority: {previewFor.rendered.priority} · est: {previewFor.rendered.estimated_hours}h
                    {previewFor.rendered.due_date && ` · due ${previewFor.rendered.due_date}`}
                  </p>
                </>
              ) : null}
            </div>
          )}
          </div>
        ))}
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && cancel()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'New Template' : 'Edit Template'}</DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground/60">
              Use {'{client}'}, {'{client.short_name}'}, {'{month}'}, {'{year}'} placeholders. They resolve when the template is applied.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Template name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Monthly Reel for {client}"
                  data-testid="task-template-form-name"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Title template (optional — falls back to name)</Label>
                <Input
                  value={form.title_template}
                  onChange={(e) => setForm((f) => ({ ...f, title_template: e.target.value }))}
                  placeholder="Monthly Reel for {client} — {month} {year}"
                  data-testid="task-template-form-title"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Description template</Label>
                <Textarea
                  rows={4}
                  value={form.description_template}
                  onChange={(e) => setForm((f) => ({ ...f, description_template: e.target.value }))}
                  placeholder="Create a 30-second reel for {client} for {month}."
                  data-testid="task-template-form-description"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Priority</Label>
                  <select
                    value={form.default_priority}
                    onChange={(e) => setForm((f) => ({ ...f, default_priority: e.target.value }))}
                    className="w-full h-9 text-[12px] rounded-md border border-input bg-background px-2"
                    data-testid="task-template-form-priority"
                  >
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Due offset (days)</Label>
                  <Input
                    type="number"
                    value={form.default_due_offset_days ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, default_due_offset_days: e.target.value }))}
                    placeholder="7"
                    data-testid="task-template-form-due-offset"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Estimated hours</Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={form.default_estimated_hours}
                    onChange={(e) => setForm((f) => ({ ...f, default_estimated_hours: e.target.value }))}
                    placeholder="2"
                    data-testid="task-template-form-estimated"
                  />
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="ghost" onClick={cancel}>Cancel</Button>
            <Button onClick={save} data-testid="task-template-form-save">
              <Save className="h-3.5 w-3.5 mr-1" />
              {editing === 'new' ? 'Create Template' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
